import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import {
  Building2, Loader2, TrendingUp, Users, Euro, Target, Award,
  Info, X, BarChart2
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import FiscalYearSelector from '../components/FiscalYearSelector';
import NegotiatorPerformanceStats from '../components/statistics/NegotiatorPerformanceStats';
import AgencyPerformanceStats from '../components/statistics/AgencyPerformanceStats';
import { formatCurrency } from '../utils/format';
import { useAgencyStats } from '../hooks/useAgencyStats';
import { dateRangesFilter, getMonthRangeDates, getMonthRanges, getPeriodRanges, isDateInRanges } from '../utils/period';

const COLORS = ['#6366F1', '#EC4899', '#10B981', '#F59E0B', '#8B5CF6', '#14B8A6', '#F43F5E', '#3B82F6'];

type Tab = 'global' | 'negotiator' | 'agency';

/* ─── InfoTooltip ─────────────────────────────────────────────────────────── */
interface InfoTooltipProps {
  title: string;
  description: string;
  formula?: string;
  example?: string;
}

function InfoTooltip({ title, description, formula, example }: InfoTooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const close = () => setIsVisible(false);

  return (
    <div className="relative">
      <button
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        onClick={() => setIsVisible(v => !v)}
        className="p-1 text-slate-400 hover:text-indigo-500 transition-colors touch-manipulation"
        aria-label="Plus d'information"
      >
        <Info size={14} />
      </button>

      {isVisible && (
        <>
          <div className="fixed inset-0 z-[9998] md:hidden" onClick={close} />
          <div className="absolute top-8 right-0 w-72 sm:w-80 bg-white rounded-2xl shadow-xl border border-slate-200 p-4 z-[9999]">
            <div className="flex items-start justify-between mb-2">
              <h4 className="font-semibold text-slate-800 text-sm pr-2">{title}</h4>
              <button onClick={close} className="md:hidden p-1 text-slate-400 hover:text-slate-600 flex-shrink-0">
                <X size={14} />
              </button>
            </div>
            <p className="text-sm text-slate-600 mb-3">{description}</p>
            {formula && (
              <div className="mb-3">
                <p className="text-xs font-medium text-slate-500 mb-1">Formule :</p>
                <div className="bg-slate-50 border border-slate-100 p-2 rounded-lg text-xs font-mono text-slate-700">{formula}</div>
              </div>
            )}
            {example && (
              <div>
                <p className="text-xs font-medium text-slate-500 mb-1">Exemple :</p>
                <p className="text-xs text-slate-600">{example}</p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/* ─── Helpers ─────────────────────────────────────────────────────────────── */
function fmt(v: number) { return formatCurrency(v); }
function fmtM(v: number): string {
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M€`;
  if (Math.abs(v) >= 1_000) return `${(v / 1_000).toFixed(0)}k€`;
  return `${v}€`;
}

const tooltipStyle = {
  backgroundColor: '#fff',
  border: '1px solid #e2e8f0',
  borderRadius: '12px',
  boxShadow: '0 8px 20px rgba(0,0,0,0.07)',
  fontSize: '12px',
};

/* ─── CompanyCard ─────────────────────────────────────────────────────────── */
interface CompanyCardProps {
  company: 'KI' | 'KA' | 'DRYADE';
  rows: { label: string; value: string; bold?: boolean }[];
  gradientClass: string;
  accentClass: string;
  onClick?: () => void;
}

function CompanyCard({ company, rows, gradientClass, accentClass, onClick }: CompanyCardProps) {
  const label = company === 'KI' ? 'Kermarrec Immobilier' : company === 'KA' ? 'Kermarrec Atlantique' : 'Dryade';
  return (
    <div
      className={`rounded-2xl bg-gradient-to-br ${gradientClass} p-5 text-white shadow-md ${onClick ? 'cursor-pointer hover:scale-[1.02] hover:shadow-lg transition-all duration-200' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-bold">{label}</h3>
          <p className="text-xs text-white/70 mt-0.5">Société {company}</p>
        </div>
        <div className={`w-10 h-10 rounded-xl ${accentClass} flex items-center justify-center`}>
          <Building2 size={20} className="text-white" />
        </div>
      </div>
      <div className="space-y-2.5">
        {rows.map((r, i) => (
          <div key={i} className={i === rows.length - 1 ? 'flex justify-between items-center pt-2 border-t border-white/20' : 'flex justify-between items-center'}>
            <span className="text-xs text-white/80">{r.label}</span>
            <span className={r.bold ? 'text-base font-bold' : 'text-sm font-semibold'}>{r.value}</span>
          </div>
        ))}
      </div>
      {onClick && (
        <p className="text-xs text-white/50 mt-3 text-right">Voir le détail →</p>
      )}
    </div>
  );
}

/* ─── CompanyDetailModal ──────────────────────────────────────────────────── */
interface CompanyDetailModalProps {
  company: 'KI' | 'KA' | 'DRYADE';
  transactions: any[];
  gradientClass: string;
  onClose: () => void;
}

function CompanyDetailModal({ company, transactions, gradientClass, onClose }: CompanyDetailModalProps) {
  const label = company === 'KI' ? 'Kermarrec Immobilier' : company === 'KA' ? 'Kermarrec Atlantique' : 'Dryade';
  const tx = transactions.filter(t => t.company === company);

  const realized   = tx.filter(t => !['potentiel', 'annule'].includes(t.status));
  const potentiel  = tx.filter(t => t.status === 'potentiel');
  const ca = realized.reduce((s, t) => {
    const retro = (t.retrocessions || []).reduce((a: number, r: any) => a + (r.amount || 0), 0);
    return s + ((t.fees_without_tax || 0) - retro);
  }, 0);
  const caHT = realized.reduce((s, t) => s + (t.fees_without_tax || 0), 0);
  const volume = realized.reduce((s, t) => s + (t.sale_price || 0), 0);
  const potCA = potentiel.reduce((s, t) => {
    const retro = (t.retrocessions || []).reduce((a: number, r: any) => a + (r.amount || 0), 0);
    return s + ((t.fees_without_tax || 0) - retro);
  }, 0);

  const statusLabel = (s: string) => {
    const map: Record<string, string> = {
      potentiel: 'Potentiel', compromis: 'Compromis', acte: 'Acte',
      encaissement: 'Encaissé', cloture: 'Clôturé', annule: 'Annulé',
    };
    return map[s] || s;
  };
  const statusColor = (s: string) => {
    if (s === 'potentiel') return 'bg-amber-100 text-amber-700';
    if (s === 'annule') return 'bg-red-100 text-red-700';
    return 'bg-emerald-100 text-emerald-700';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <div
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`bg-gradient-to-br ${gradientClass} px-6 py-5 text-white flex items-center justify-between`}>
          <div>
            <h2 className="text-lg font-bold">{label}</h2>
            <p className="text-xs text-white/70 mt-0.5">Détail du Chiffre d'affaires</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-xl bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-3 gap-3 p-5 border-b border-slate-100">
          <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3.5">
            <p className="text-xs text-emerald-600 mb-1 font-medium">CA Net HT</p>
            <p className="text-lg font-bold text-emerald-900 leading-tight">{fmt(ca)}</p>
            <p className="text-xs text-emerald-600/70 mt-0.5">Honoraires HT : {fmt(caHT)}</p>
          </div>
          <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-3.5">
            <p className="text-xs text-indigo-600 mb-1 font-medium">Ventes réalisées</p>
            <p className="text-lg font-bold text-indigo-900">{realized.length}</p>
            <p className="text-xs text-indigo-600/70 mt-0.5">Vol. {fmtM(volume)}</p>
          </div>
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-3.5">
            <p className="text-xs text-amber-600 mb-1 font-medium">Potentiel</p>
            <p className="text-lg font-bold text-amber-900">{potentiel.length}</p>
            <p className="text-xs text-amber-600/70 mt-0.5">{fmt(potCA)} estimé</p>
          </div>
        </div>

        {/* Transaction list */}
        <div className="flex-1 overflow-y-auto">
          {tx.length === 0 ? (
            <p className="text-center text-slate-400 text-sm py-10">Aucune transaction sur la période</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500">Bien</th>
                  <th className="text-left px-3 py-3 text-xs font-semibold text-slate-500">Négociateur(s)</th>
                  <th className="text-left px-3 py-3 text-xs font-semibold text-slate-500">Compromis</th>
                  <th className="text-left px-3 py-3 text-xs font-semibold text-slate-500">Statut</th>
                  <th className="text-right px-3 py-3 text-xs font-semibold text-slate-500">Prix vente</th>
                  <th className="text-right px-5 py-3 text-xs font-semibold text-slate-500">Net HT</th>
                </tr>
              </thead>
              <tbody>
                {tx.map((t, i) => {
                  const retro = (t.retrocessions || []).reduce((a: number, r: any) => a + (r.amount || 0), 0);
                  const net = (t.fees_without_tax || 0) - retro;
                  const negoNames = (t.negotiators || []).map((n: any) => n.name).join(', ');
                  const compromisDate = t.compromise_date
                    ? new Date(t.compromise_date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
                    : '—';
                  return (
                    <tr key={t.id} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                      <td className="px-5 py-3 font-medium text-slate-800">
                        {t.property_number || '—'}
                        {t.seller && <span className="block text-xs text-slate-400 truncate max-w-[120px]">{t.seller}</span>}
                      </td>
                      <td className="px-3 py-3 text-slate-700 max-w-[140px]">
                        {negoNames || <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-3 py-3 text-slate-500 whitespace-nowrap">{compromisDate}</td>
                      <td className="px-3 py-3">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${statusColor(t.status)}`}>
                          {statusLabel(t.status)}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-right text-slate-600 whitespace-nowrap">{fmtM(t.sale_price || 0)}</td>
                      <td className="px-5 py-3 text-right font-semibold text-slate-800 whitespace-nowrap">{fmt(net)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── SectionDivider ──────────────────────────────────────────────────────── */
function SectionDivider({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <div className="flex items-center gap-3">
      <div className="h-px flex-1 bg-slate-200" />
      <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-slate-400">
        {icon}{label}
      </span>
      <div className="h-px flex-1 bg-slate-200" />
    </div>
  );
}

/* ─── CardShell ───────────────────────────────────────────────────────────── */
function CardShell({
  icon, iconBg, title, extra, children, chartHeight
}: {
  icon: React.ReactNode;
  iconBg: string;
  title: string;
  extra?: React.ReactNode;
  children: React.ReactNode;
  chartHeight?: string;
}) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-lg ${iconBg} flex items-center justify-center`}>{icon}</div>
          <span className="text-sm font-semibold text-slate-700">{title}</span>
        </div>
        {extra && <div className="text-xs text-slate-400">{extra}</div>}
      </div>
      <div className={`p-5 ${chartHeight ?? ''}`}>{children}</div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
export default function Statistics() {
  const [activeTab, setActiveTab] = useState<Tab>('global');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [detailCompany, setDetailCompany] = useState<'KI' | 'KA' | 'DRYADE' | null>(null);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [fiscalYear, setFiscalYear] = useState<number>(() => {
    const m = new Date().getMonth() + 1;
    return m < 4 ? new Date().getFullYear() - 1 : new Date().getFullYear();
  });
  const [period, setPeriod] = useState<'year' | 'quarter' | 'month'>('year');
  const [selectedPeriod, setSelectedPeriod] = useState('1');

  const { agencies, loadData: loadAgencyData } = useAgencyStats();

  useEffect(() => { loadData(); }, [fiscalYear, period, selectedPeriod]);

  const loadData = async () => {
    try {
      setLoading(true);
      let startDate: string, endDate: string;

      if (period === 'year') {
        startDate = `${fiscalYear}-04-01`;
        endDate   = `${fiscalYear + 1}-03-31`;
      } else if (period === 'quarter') {
        const qMap = { '1': ['04','06'], '2': ['07','09'], '3': ['10','12'], '4': ['01','03'] };
        const [s, e] = qMap[selectedPeriod as keyof typeof qMap];
        const y = selectedPeriod === '4' ? fiscalYear + 1 : fiscalYear;
        startDate = `${y}-${s}-01`;
        endDate   = `${y}-${e}-${new Date(y, parseInt(e), 0).getDate()}`;
      } else {
        ({ startDate, endDate } = getMonthRangeDates(selectedPeriod, fiscalYear));
      }

      const ranges = getPeriodRanges(period, selectedPeriod, fiscalYear, startDate, endDate);

      await loadAgencyData(startDate, endDate, ranges);

      const { data, error: err } = await supabase
        .from('transactions')
        .select(`
          id, property_number, seller, buyer, sale_price,
          fees_with_tax, fees_without_tax, mandate_type, property_origin,
          status, compromise_date, expected_acte_date, regul, commentaire_regul,
          invoice_date, encaissement_date,
          transaction_agencies(agency, agencies(company)),
          transaction_negotiators(negotiator:negotiators(id,firstname,lastname), role, percentage),
          transaction_assistants(assistant:assistants(id,firstname,lastname), role),
          transaction_retrocessions(id, type, amount)
        `)
        .or(
          ['compromise_date', 'invoice_date', 'encaissement_date']
            .map(column => dateRangesFilter(column, ranges))
            .join(',')
        );

      if (err) throw err;

      setTransactions((data || []).map(t => ({
        ...t,
        company: t.transaction_agencies[0]?.agencies?.company || 'Unknown',
        negotiators: (t.transaction_negotiators || []).map((tn: any) => ({
          id: tn.negotiator.id,
          name: `${tn.negotiator.firstname} ${tn.negotiator.lastname}`,
          role: tn.role, percentage: tn.percentage
        })),
        assistants: (t.transaction_assistants || []).map((ta: any) => ({
          id: ta.assistant.id,
          name: `${ta.assistant.firstname} ${ta.assistant.lastname}`,
          role: ta.role
        })),
        retrocessions: (t.transaction_retrocessions || []).map((tr: any) => ({
          id: tr.id, type: tr.type, amount: tr.amount
        }))
      })));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodChange = (year: number, newPeriod: 'year' | 'quarter' | 'month', newSP: string) => {
    setFiscalYear(year);
    if (newPeriod !== period) {
      if (newPeriod === 'month') {
        const m = new Date().getMonth() + 1;
        setSelectedPeriod(m >= 4 ? String(m).padStart(2, '0') : '04');
      } else {
        setSelectedPeriod('1');
      }
      setPeriod(newPeriod);
    } else {
      setPeriod(newPeriod);
      setSelectedPeriod(newSP);
    }
  };

  const getFilterRange = () => {
    if (period === 'year') return { start: new Date(fiscalYear, 3, 1), end: new Date(fiscalYear + 1, 2, 31) };
    const m: Record<string, { sm: number; em: number; yo: number }> = {
      '1': { sm: 3, em: 5, yo: 0 }, '2': { sm: 6, em: 8, yo: 0 },
      '3': { sm: 9, em: 11, yo: 0 }, '4': { sm: 0, em: 2, yo: 1 }
    };
    const q = m[selectedPeriod] ?? m['1'];
    const y = fiscalYear + q.yo;
    return { start: new Date(y, q.sm, 1), end: new Date(y, q.em + 1, 0) };
  };

  const isInPeriod = (date: string) => {
    if (period === 'month') return isDateInRanges(date, getMonthRanges(selectedPeriod, fiscalYear));
    const { start, end } = getFilterRange();
    const d = new Date(date);
    return d >= start && d <= end;
  };

  /* ── production subset: réalisées filtrées par compromise_date, potentiel toujours incluses ── */
  const prodTx = transactions.filter(t => {
    if (t.status === 'potentiel') return true;
    if (!t.compromise_date) return false;
    return isInPeriod(t.compromise_date);
  });

  /* ── derived totals ── */
  const realized   = prodTx.filter(t => !['potentiel', 'annule'].includes(t.status)).length;
  const totalTx    = prodTx.length;
  const totalVolume = prodTx.reduce((s, t) => s + t.sale_price, 0);
  const totalNet   = prodTx.reduce((s, t) => {
    const r = (t.retrocessions || []).reduce((a: number, r: any) => a + r.amount, 0);
    return s + (t.fees_without_tax - r);
  }, 0);

  /* ── company stats helper ── */
  const companyStats = (company: string) => {
    const tx    = prodTx.filter(t => t.company === company);
    const vol   = tx.reduce((s, t) => s + t.sale_price, 0);
    const fees  = tx.reduce((s, t) => s + (t.fees_without_tax || 0), 0);
    const retro = tx.reduce((s, t) => s + (t.retrocessions || []).reduce((a: number, r: any) => a + (r.amount || 0), 0), 0);
    return { tx, count: tx.length, vol, fees, retro, net: fees - retro };
  };

  /* ── loading ── */
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
        <p className="text-sm text-slate-500">Chargement des statistiques…</p>
      </div>
    );
  }

  /* ══════════════════════════════════════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════════════════════════════════════ */
  return (
    <div className="space-y-6 pb-6">

      {/* ── Header ── */}
      <div className="relative rounded-2xl overflow-hidden bg-gradient-to-r from-slate-800 via-slate-900 to-indigo-950 shadow-lg">
        <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 rounded-full -translate-y-1/2 translate-x-1/3 pointer-events-none" />
        <div className="absolute bottom-0 right-32 w-40 h-40 bg-violet-500/10 rounded-full translate-y-1/2 pointer-events-none" />

        {/* Title row */}
        <div className="relative px-7 pt-6 pb-4 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center shrink-0">
              <BarChart2 size={22} className="text-white/80" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white tracking-tight">Statistiques</h1>
              <p className="text-sm text-slate-400 mt-0.5">
                <span className="text-indigo-300 font-semibold">{totalTx}</span>
                {' '}transaction{totalTx !== 1 ? 's' : ''} sur la période
              </p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <FiscalYearSelector
              value={fiscalYear}
              period={period}
              selectedPeriod={selectedPeriod}
              onChange={handlePeriodChange}
              variant="dark"
            />
          </div>
        </div>

        {/* Tab bar */}
        <div className="relative px-7 pb-4 flex items-center gap-1 border-t border-white/10 pt-3">
          {([
            { id: 'global'     as Tab, label: 'Vue Globale',    Icon: BarChart2 },
            { id: 'negotiator' as Tab, label: 'Négociateurs',   Icon: Users     },
            { id: 'agency'     as Tab, label: 'Agences',        Icon: Building2 },
          ] as const).map(({ id, label, Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
                activeTab === id
                  ? 'bg-white text-slate-800 shadow-sm'
                  : 'text-white/55 hover:text-white hover:bg-white/10'
              }`}
            >
              <Icon size={15} />
              {label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* ══════════════════════════════ GLOBAL TAB ══════════════════════════ */}
      {activeTab === 'global' && (
        <div className="space-y-6">

          {/* ── Section: Production ── */}
          <SectionDivider icon={<TrendingUp size={12} />} label="Production" />

          {/* KPIs */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

            {/* Transactions */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
              <div className="flex items-start justify-between mb-4">
                <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center">
                  <Users size={18} className="text-indigo-600" />
                </div>
                <div className="flex items-center gap-1">
                  <div className="text-right">
                    <span className="text-2xl font-bold text-slate-800">{realized}</span>
                    <span className="text-xs text-slate-400 ml-1">réalisées</span>
                  </div>
                  <InfoTooltip
                    title="Transactions réalisées"
                    description="Transactions dont le statut est Compromis, Acté, Signé ou Clôturé. Les annulées sont exclues."
                    formula="Réalisées: status ∉ ['potentiel', 'annule'] | Potentiel: status='potentiel'"
                    example="15 potentielles + 23 réalisées"
                  />
                </div>
              </div>
              <p className="text-sm font-semibold text-slate-700">Transactions</p>
              <p className="text-xs text-slate-400 mt-0.5">
                Potentiel : <span className="font-semibold text-amber-500">{prodTx.filter(t => t.status === 'potentiel').length}</span>
              </p>
              <div className="mt-3 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-indigo-500 rounded-full transition-all"
                  style={{ width: totalTx > 0 ? `${(realized / totalTx) * 100}%` : '0%' }}
                />
              </div>
            </div>

            {/* Net HT */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
              <div className="flex items-start justify-between mb-4">
                <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center">
                  <Target size={18} className="text-violet-600" />
                </div>
                <div className="flex items-center gap-1">
                  <p className="text-xl font-bold text-slate-800">
                    <span className="sm:hidden">{fmtM(totalNet)}</span>
                    <span className="hidden sm:inline">{fmt(totalNet)}</span>
                  </p>
                  <InfoTooltip
                    title="Honoraires Net HT"
                    description="CA réel après déduction de toutes les rétrocessions."
                    formula="Σ(Honoraires HT − Rétrocessions)"
                    example="10 000€ − 150€ = 9 850€ net"
                  />
                </div>
              </div>
              <p className="text-sm font-semibold text-slate-700">Honoraires Net HT</p>
              <p className="text-xs text-slate-400 mt-0.5">Chiffre d'affaires réel</p>
              <div className="mt-3 h-1.5 bg-violet-100 rounded-full overflow-hidden">
                <div className="h-full bg-violet-500 rounded-full w-full" />
              </div>
            </div>
          </div>

          {/* Cartes par société — Production */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {(['KI', 'KA', 'DRYADE'] as const).map(company => {
              const s = companyStats(company);
              return (
                <CompanyCard
                  key={company}
                  company={company}
                  gradientClass={
                    company === 'KI' ? 'from-amber-500 to-yellow-400' :
                    company === 'KA' ? 'from-emerald-500 to-green-400' :
                    'from-orange-500 to-amber-400'
                  }
                  accentClass={
                    company === 'KI' ? 'bg-amber-400/30' :
                    company === 'KA' ? 'bg-emerald-400/30' :
                    'bg-orange-400/30'
                  }
                  rows={[
                    { label: 'Transactions', value: String(s.count) },
                    { label: 'Volume',         value: fmt(s.vol) },
                    { label: 'Honoraires HT',  value: fmt(s.fees) },
                    { label: 'Rétrocession',   value: fmt(s.retro) },
                    { label: 'Net HT',         value: fmt(s.net), bold: true },
                  ]}
                  onClick={() => setDetailCompany(company)}
                />
              );
            })}
          </div>

          {/* Rétrocessions Lefeuvre */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-4 bg-violet-50 border-b border-violet-100">
              <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center">
                <Building2 size={16} className="text-violet-600" />
              </div>
              <span className="text-sm font-semibold text-violet-800">Rétrocessions Lefeuvre</span>
            </div>
            <div className="p-5">
              {(() => {
                const stats = transactions.filter(t => t.status !== 'potentiel').reduce(
                  (acc, t) => {
                    const retros = (t.retrocessions || []).filter((r: any) => r.type === 'retrocession_lefeuvre');
                    const total  = retros.reduce((s: number, r: any) => s + r.amount, 0);
                    if (total > 0) {
                      acc.count++;
                      acc.amount += total;
                      acc.volume += t.sale_price;
                      acc.fees   += t.fees_without_tax;
                    }
                    return acc;
                  },
                  { count: 0, amount: 0, volume: 0, fees: 0 }
                );
                const pct = stats.fees > 0 ? (stats.amount / stats.fees) * 100 : 0;

                return (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-3.5">
                      <p className="text-xs text-indigo-600 mb-1">Transactions</p>
                      <p className="text-lg font-bold text-indigo-900">{stats.count}</p>
                    </div>
                    <div className="bg-amber-50 border border-amber-100 rounded-xl p-3.5">
                      <p className="text-xs text-amber-600 mb-1">Total Rétro.</p>
                      <p className="text-lg font-bold text-amber-900">
                        <span className="sm:hidden">{fmtM(stats.amount)}</span>
                        <span className="hidden sm:inline">{fmt(stats.amount)}</span>
                      </p>
                    </div>
                    <div className="bg-blue-50 border border-blue-100 rounded-xl p-3.5">
                      <p className="text-xs text-blue-600 mb-1">Volume</p>
                      <p className="text-lg font-bold text-blue-900">
                        <span className="sm:hidden">{fmtM(stats.volume)}</span>
                        <span className="hidden sm:inline">{fmt(stats.volume)}</span>
                      </p>
                    </div>
                    <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3.5">
                      <p className="text-xs text-emerald-600 mb-1">% Rétrocession</p>
                      <p className="text-lg font-bold text-emerald-900">{pct.toFixed(1)}%</p>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Performance par société — bar chart */}
          <CardShell
            icon={<BarChart2 size={16} className="text-indigo-600" />}
            iconBg="bg-indigo-50"
            title="Performance par société"
            extra="CA Net HT vs Objectif"
            chartHeight="h-[260px] sm:h-[340px]"
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={(['KI', 'KA', 'DRYADE'] as const).map(company => {
                  const s = companyStats(company);
                  const obj = agencies.filter((a: any) => a.company === company).reduce((sum: number, a: any) => sum + a.annual_objective, 0);
                  return {
                    name: company === 'DRYADE' ? 'Dryade' : company,
                    netFees: s.net,
                    objective: obj,
                  };
                })}
                margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={{ stroke: '#e2e8f0' }} tickLine={false} />
                <YAxis tickFormatter={v => fmtM(v)} tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} width={48} />
                <Tooltip formatter={(v: any, n: string) => [fmt(v), n]} contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: '11px' }} />
                <Bar name="Honoraires Net HT" dataKey="netFees"   fill="#6366F1" radius={[6, 6, 0, 0]} />
                <Bar name="Objectif"           dataKey="objective" fill="#e2e8f0" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardShell>

          {/* Mandats + Top 5 agences */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

            {/* Types de mandats — donut */}
            <CardShell
              icon={<Award size={16} className="text-violet-600" />}
              iconBg="bg-violet-50"
              title="Types de mandats"
              extra={
                <InfoTooltip
                  title="Types de mandats"
                  description="Répartition des transactions par type de mandat."
                  formula="Exclusif / Simple / Privilège"
                />
              }
              chartHeight="h-[220px] sm:h-[280px]"
            >
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Exclusif',  value: transactions.filter(t => t.mandate_type === 'exclu').length },
                      { name: 'Simple',    value: transactions.filter(t => t.mandate_type === 'simple').length },
                      { name: 'Privilège', value: transactions.filter(t => t.mandate_type === 'privilège').length },
                    ]}
                    dataKey="value"
                    nameKey="name"
                    cx="50%" cy="50%"
                    outerRadius="75%" innerRadius="38%"
                    paddingAngle={3} strokeWidth={0}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {[0, 1, 2].map(i => <Cell key={i} fill={COLORS[i]} />)}
                  </Pie>
                  <Tooltip formatter={(v: any) => [`${v} transactions`]} contentStyle={tooltipStyle} />
                </PieChart>
              </ResponsiveContainer>
            </CardShell>

            {/* Top 5 agences */}
            <CardShell
              icon={<Award size={16} className="text-amber-600" />}
              iconBg="bg-amber-50"
              title="Top 5 Agences"
              extra={
                <InfoTooltip
                  title="Top 5 Agences"
                  description="Agences classées par honoraires TTC générés sur la période."
                  formula="Classement par honoraires TTC"
                />
              }
            >
              <div className="space-y-3">
                {(() => {
                  const aStats = transactions.reduce((acc, t) => {
                    (t.transaction_agencies || []).forEach((ta: any) => {
                      if (!acc[ta.agency]) acc[ta.agency] = { count: 0, volume: 0, fees: 0 };
                      acc[ta.agency].count++;
                      acc[ta.agency].volume += t.sale_price;
                      acc[ta.agency].fees   += t.fees_with_tax;
                    });
                    return acc;
                  }, {} as Record<string, any>);

                  const entries = Object.entries(aStats)
                    .sort(([, a], [, b]) => (b as any).fees - (a as any).fees)
                    .slice(0, 5);

                  const maxFees = entries[0] ? (entries[0][1] as any).fees : 1;
                  const rankCls = ['bg-amber-400', 'bg-slate-400', 'bg-orange-600', 'bg-indigo-400', 'bg-indigo-300'];

                  return entries.map(([agency, s], i) => (
                    <div key={agency}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0 ${rankCls[i]}`}>
                            {i + 1}
                          </div>
                          <p className="font-medium text-slate-800 text-sm truncate">{agency}</p>
                        </div>
                        <p className="font-bold text-slate-800 text-sm ml-3 flex-shrink-0">
                          <span className="sm:hidden">{fmtM((s as any).fees)}</span>
                          <span className="hidden sm:inline">{fmt((s as any).fees)}</span>
                        </p>
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-indigo-400 rounded-full"
                          style={{ width: `${((s as any).fees / maxFees) * 100}%` }}
                        />
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </CardShell>
          </div>

          {/* ── Section: Comptabilité ── */}
          <SectionDivider icon={<Euro size={12} />} label="Comptabilité" />

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {(['KI', 'KA', 'DRYADE'] as const).map(company => {
              const tx = transactions.filter(t => t.company === company);
              const validStatus = (s: string) => {
                const v = (s || '').toLowerCase();
                return v.includes('comprom') || v.includes('acte') || v.includes('encais') || v.includes('clotur');
              };
              const invoiced = tx.reduce((sum, t) => {
                if (!t.invoice_date || !validStatus(t.status)) return sum;
                if (!isInPeriod(t.invoice_date)) return sum;
                return sum + (t.fees_without_tax || 0);
              }, 0);
              const encaisse = tx.reduce((sum, t) => {
                if (!t.encaissement_date || !validStatus(t.status)) return sum;
                if (!isInPeriod(t.encaissement_date)) return sum;
                const retro = (t.retrocessions || []).reduce((a: number, r: any) => a + (r.amount || 0), 0);
                return sum + ((t.fees_without_tax || 0) - retro);
              }, 0);

              return (
                <CompanyCard
                  key={company}
                  company={company}
                  gradientClass={
                    company === 'KI' ? 'from-amber-500 to-yellow-400' :
                    company === 'KA' ? 'from-emerald-500 to-green-400' :
                    'from-orange-500 to-amber-400'
                  }
                  accentClass={
                    company === 'KI' ? 'bg-amber-400/30' :
                    company === 'KA' ? 'bg-emerald-400/30' :
                    'bg-orange-400/30'
                  }
                  rows={[
                    { label: 'HT Net facturé',  value: fmt(invoiced) },
                    { label: 'HT Net encaissé', value: fmt(encaisse), bold: true },
                  ]}
                  onClick={() => setDetailCompany(company)}
                />
              );
            })}
          </div>

        </div>
      )}

      {activeTab === 'negotiator' && (
        <NegotiatorPerformanceStats fiscalYear={fiscalYear} period={period} selectedPeriod={selectedPeriod} />
      )}
      {activeTab === 'agency' && (
        <AgencyPerformanceStats fiscalYear={fiscalYear} period={period} selectedPeriod={selectedPeriod} />
      )}

      {/* ── Modal détail CA ── */}
      {detailCompany && (
        <CompanyDetailModal
          company={detailCompany}
          transactions={prodTx}
          gradientClass={
            detailCompany === 'KI' ? 'from-amber-500 to-yellow-400' :
            detailCompany === 'KA' ? 'from-emerald-500 to-green-400' :
            'from-orange-500 to-amber-400'
          }
          onClose={() => setDetailCompany(null)}
        />
      )}

    </div>
  );
}
